import { createGlobalStyle } from 'styled-components';

import { ramps } from './theme';

// Rendered outside the ThemeProvider, so it can't read the MUI theme — reference
// the static design ramps directly (mode-independent neutral scrollbar).
const HANDLE = ramps.neutral[500];
const HANDLE_HOVER = ramps.neutral[800];

// Track width, and the gap between the handle and the edge of that track. The
// handle itself is therefore SIZE − 2 × PADDING wide.
const SIZE = 12;
const PADDING = 2;
const HANDLE_RADIUS = 4;

// Every scroll container in the app renders the same neutral, rail-less handle,
// whichever mechanism paints it:
//
//  1. `Scrollbar` (OverlayScrollbars) — used for the page scroll, where an
//     overlay handle of zero layout width means showing/hiding it never reflows
//     the centred content. Themed via `os-theme-cognito` below.
//  2. `::-webkit-scrollbar` — everything else: cards, tables, modals, menus and
//     third-party widgets, whose native scrollbars would otherwise render with a
//     filled track and stepper arrows. A transparent handle border plus
//     `background-clip: padding-box` insets the handle the way
//     `--os-padding-perpendicular` does in (1); the outer radius is grown by the
//     border width so the visible corner still reads as HANDLE_RADIUS.
//  3. `scrollbar-width` / `scrollbar-color` — the standard properties, for
//     engines without (2). Blink/WebKit ignore `::-webkit-scrollbar` entirely
//     once either is set to a non-`auto` value, and their `thin` rendering keeps
//     the stepper arrows, so these are gated behind the absence of (2) rather
//     than declared alongside it.
//
// (2) and (3) both replace the platform scrollbar with a classic one that
// reserves SIZE of layout width. Touch platforms instead overlay a self-hiding
// scrollbar that costs no width at all — the same reason PageLayout drops to
// `ContentScrollNative` below `sm` — and offer no cursor to drag a handle with,
// so both are scoped to devices that have one and left native everywhere else.
export default createGlobalStyle`
  .os-theme-cognito {
    --os-size: ${SIZE}px;
    --os-padding-perpendicular: ${PADDING}px;
    --os-padding-axis: ${PADDING}px;
    --os-handle-border-radius: ${HANDLE_RADIUS}px;
    --os-handle-bg: ${HANDLE};
    --os-handle-bg-hover: ${HANDLE_HOVER};
    --os-handle-bg-active: ${HANDLE_HOVER};
    --os-handle-min-size: 33px;
  }

  @media (hover: hover) and (pointer: fine) {
    *::-webkit-scrollbar {
      width: ${SIZE}px;
      height: ${SIZE}px;
    }

    *::-webkit-scrollbar-track,
    *::-webkit-scrollbar-corner {
      background: transparent;
    }

    *::-webkit-scrollbar-button {
      display: none;
    }

    *::-webkit-scrollbar-thumb {
      background-color: ${HANDLE};
      background-clip: padding-box;
      border: ${PADDING}px solid transparent;
      border-radius: ${HANDLE_RADIUS + PADDING}px;
    }

    *::-webkit-scrollbar-thumb:hover,
    *::-webkit-scrollbar-thumb:active {
      background-color: ${HANDLE_HOVER};
    }

    @supports not selector(::-webkit-scrollbar) {
      * {
        scrollbar-width: thin;
        scrollbar-color: ${HANDLE} transparent;
      }
    }
  }
`;
